import type { App2WorklistQuery } from 'types/graphql'

import { Chip, Link, Text, type ChipTone } from 'src/design-system/ui'
import { sentinelCategoryLabel } from 'src/lib/evieSentinelLabels'
import { getRelativeTimeFromNow } from 'src/lib/timelineUtils'
import { routes } from 'src/platform/router'

const severityTone: Record<string, ChipTone> = {
  critical: 'error',
  intervene: 'error',
  watch: 'warning',
  info: 'info',
}

export const App2FleetTable = ({
  rows,
  label,
  onOpen,
  onNavigate,
}: {
  rows: App2WorklistQuery['app2Worklist']['items']
  label: string
  onOpen: (href: string) => void
  onNavigate: (event: React.MouseEvent<Element>, href: string) => void
}) => (
  <div className="ds:overflow-x-auto">
    <table aria-label={label} className="app2-fleet-table ui-type-caption">
      <thead>
        <tr>
          {['Case', 'Top finding', 'Status', 'Days quiet', 'Flags', 'PM'].map(
            (heading) => (
              <th className="ui-type-caption-strong" scope="col" key={heading}>
                {heading}
              </th>
            )
          )}
        </tr>
      </thead>
      <tbody>
        {rows.map((item) => {
          const href = routes.app2({ applicationId: item.applicationId })
          return (
            <tr
              key={item.applicationId}
              data-case
              onClick={(event) => {
                if (
                  event.target instanceof Element &&
                  event.target.closest('a')
                )
                  return
                onOpen(href)
              }}
            >
              <td>
                <Link href={href} onClick={(event) => onNavigate(event, href)}>
                  #{item.applicationId}
                </Link>
              </td>
              <td>
                {item.topFindingSeverity ? (
                  <div className="ds:flex ds:items-center ds:flex-wrap ds:gap-2">
                    <Chip
                      size="sm"
                      tone={severityTone[item.topFindingSeverity] ?? 'neutral'}
                    >
                      {item.topFindingSeverity}
                    </Chip>
                    <span
                      title={item.topFindingCategory ?? undefined}
                      data-testid={`worklist-category-${item.topFindingCategory ?? 'none'}`}
                    >
                      <Chip size="sm">
                        {item.topFindingLabel ??
                          sentinelCategoryLabel(item.topFindingCategory) ??
                          '—'}
                      </Chip>
                    </span>
                    <span
                      title={item.topFindingTitle ?? undefined}
                      className="ds:truncate"
                    >
                      {item.topFindingTitle}
                    </span>
                    {item.clockDebtLabel && (
                      <Text variant="micro" tone="muted">
                        {item.clockDebtLabel}
                      </Text>
                    )}
                    {item.openFindingCount > 1 && (
                      <Text variant="micro" tone="muted">
                        +{item.openFindingCount - 1} more
                      </Text>
                    )}
                  </div>
                ) : (
                  '—'
                )}
              </td>
              <td>{item.twoDotsStatus ?? '—'}</td>
              <td>{item.daysSinceLastChange}</td>
              <td>
                <div className="ds:flex ds:flex-wrap ds:gap-2">
                  {item.needsAttentionAt && (
                    <span title={getRelativeTimeFromNow(item.needsAttentionAt)}>
                      <Chip size="sm" tone="warning">
                        needs attention
                      </Chip>
                    </span>
                  )}
                  {item.evieAdvisedClosedAt && (
                    <span
                      title={getRelativeTimeFromNow(item.evieAdvisedClosedAt)}
                    >
                      <Chip size="sm">advised closed</Chip>
                    </span>
                  )}
                </div>
              </td>
              <td>{item.pmName}</td>
            </tr>
          )
        })}
      </tbody>
    </table>
  </div>
)
